import React, { Suspense, lazy, useEffect } from "react";
import { BrowserRouter, Link, Navigate, Route, Routes, useLocation, useNavigate, useParams } from "react-router-dom";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { ArtistProvider, useArtistScopeSafe } from "./lib/artistContext";
import { ToastProvider } from "./components/ToastProvider";
import TopNav from "./components/TopNav";
import { AuthProvider, useAuth } from "./lib/authContext";
import { SessionUserProvider, useSessionUser } from "./hooks/useSessionUser";
import { CapabilityProvider } from "./providers/CapabilitiesProvider";
import CapabilityGate from "./components/CapabilityGate";
import LockedFeatureCard from "./components/LockedFeatureCard";
import ProtectedRoute from "./components/auth/ProtectedRoute";
import OwnerRoute from "./components/auth/OwnerRoute";
import Loading from "./components/Loading";
import ScrollToTop from "./components/ScrollToTop";
import { BreadcrumbProvider } from "./providers/BreadcrumbProvider";
import { SwrProvider } from "./providers/SwrProvider";
import { I18nProvider } from "./providers/I18nProvider";
import { FeatureFlagsProvider } from "./providers/FeatureFlagsProvider";
import { PodcastTourProvider } from "./providers/PodcastTourProvider";
import { useOnboardingProgress } from "./hooks/useOnboardingProgress";
import { ActiveAgentContextProvider } from "./context/ActiveAgentContext";
import { ThemeProvider } from "./providers/ThemeProvider";
import { fetchCurrentAccount } from "./api/account";
import { hasActiveSubscription, needsBillingContextRefresh, requiresBillingBeforeOnboarding } from "./lib/billingGate";
const ExecutiveDashboard = lazy(() => import("./pages/ExecutiveDashboard"));
const MasterControlPage = lazy(() => import("./pages/MasterControl"));
const InvitesPage = lazy(() => import("./pages/master_control/InvitesPage"));
const RegistryPage = lazy(() => import("./pages/master_control/RegistryPage"));
const WorkflowBuilder = lazy(() => import("./pages/WorkflowBuilder"));
const WorkflowTriggerHistoryPage = lazy(() => import("./pages/WorkflowTriggerHistoryPage"));
const AgentsHubPage = lazy(() => import("./pages/AgentsHubPage"));
const AgentPage = lazy(() => import("./pages/AgentPage"));
const EmailPage = lazy(() => import("./pages/EmailPage"));
const HitlInboxPage = lazy(() => import("./pages/HitlInbox"));
const AgentsObservabilityDashboard = lazy(() => import("./pages/AgentsObservabilityDashboard"));
const MemoryConsolePage = lazy(() => import("./pages/MemoryConsole"));
const FrontendErrorsPage = lazy(() => import("./pages/observability/FrontendErrorsPage"));
const RealTimeFrontendErrorsPage = lazy(() => import("./pages/observability/RealTimeFrontendErrorsPage"));
const CrmPage = lazy(() => import("./pages/CrmPage"));
const ArtistsPage = lazy(() => import("./pages/ArtistsPage"));
const BookingPage = lazy(() => import("./pages/BookingPage"));
const PublicVenuePage = lazy(() => import("./pages/discover/PublicVenuePage"));
const LeadDetailPage = lazy(() => import("./pages/LeadDetailPage"));
const EventDetailPage = lazy(() => import("./pages/EventDetailPage"));
const VenueDetailPage = lazy(() => import("./pages/VenueDetailPage"));
const CalendarPage = lazy(() => import("./pages/CalendarPage"));
const VenueMapPage = lazy(() => import("./pages/VenueMap"));
const VenueQueuePage = lazy(() => import("./pages/VenueQueue"));
const VenueIngestionPage = lazy(() => import("./pages/VenueIngestion"));
const RoutingPage = lazy(() => import("./pages/Routing/RoutingPage"));
const AccountingPage = lazy(() => import("./pages/AccountingPage"));
const MixboardPage = lazy(() => import("./pages/MixboardPage"));
const MarketingLibraryPage = lazy(() => import("./pages/MarketingPage"));
const MusicAnalyticsPage = lazy(() => import("./pages/MusicAnalytics/MusicAnalyticsPage"));
const MusicAnalyticsTrackPage = lazy(() => import("./pages/MusicAnalytics/TrackDetailPage"));
const PressroomPage = lazy(() => import("./pages/PressroomPage"));
const OrgSettingsPage = lazy(() => import("./pages/OrgSettingsPage"));
const ArtistSettingsPage = lazy(() => import("./pages/settings/ArtistSettingsPage"));
const BillingSettingsPage = lazy(() => import("./pages/settings/BillingSettingsPage"));
const EmailSettingsPage = lazy(() => import("./pages/settings/EmailSettingsPage"));
const AccountSettingsHubPage = lazy(() => import("./pages/AccountSettingsHubPage"));
const CollectorAuthorize = lazy(() => import('./pages/CollectorAuthorize'));
const CollectorConnect = lazy(() => import('./pages/CollectorConnect'));
const LoginPage = lazy(() => import("./pages/LoginPage"));
const ForgotPasswordPage = lazy(() => import("./pages/ForgotPasswordPage"));
const ResetPasswordPage = lazy(() => import("./pages/ResetPasswordPage"));
const TwoFactorPage = lazy(() => import("./pages/TwoFactorPage"));
const OnboardingFlow = lazy(() => import("./pages/onboarding/OnboardingFlow"));
const ClaimInvitePage = lazy(() => import("./pages/ClaimInvitePage"));
const NotFoundPage = lazy(() => import("./pages/NotFound"));
const ArtistNavLanding = lazy(() => import("./pages/HelloLandingBeta"));
const BetaAdminPage = lazy(() => import("./pages/BetaAdminPage"));
const BetaJoinPage = lazy(() => import("./pages/BetaJoinPage"));
const BetaApplyPage = lazy(() => import("./pages/BetaApplyPage"));
const HelloLanding = lazy(() => import("./pages/HelloLanding"));
// Private-beta landing DESIGN CANDIDATE: dev server only. `import.meta.env.DEV`
// is statically false in production builds, so this import (and the route
// below) is dropped from the bundle entirely.
const HelloLandingBeta = import.meta.env.DEV ? lazy(() => import("./pages/HelloLandingBeta")) : null;
const NotFoundPublic = lazy(() => import("./pages/NotFoundPublic"));
const DashboardPage = lazy(() => import("./pages/Dashboard"));
const DiscoverPage = lazy(() => import("./pages/discover/DiscoverPage"));

const PLUS_CAPABILITY = "plan.plus";

const AGENT_ROUTE_CONFIG: { name: string; capability: string | null; ownerOnly?: boolean }[] = [
  { name: "manager",   capability: "canUseManagerAgent" },
  { name: "booking",   capability: "canUseBookingAI" },
  { name: "marketing", capability: "canUseMarketingAI" },
  { name: "publicist", capability: "canUsePublicistAI" },
  { name: "email",     capability: "canUseEmailAI" },
  { name: "podcast",   capability: null, ownerOnly: true },
];

function PlusTierGate({ children }: { children: React.ReactNode }) {
  const { user } = useSessionUser();
  if (user?.is_owner) {
    return <>{children}</>;
  }
  return (
    <CapabilityGate
      capability={PLUS_CAPABILITY}
      variant="card"
      fallback={
        <LockedFeatureCard
          flag={PLUS_CAPABILITY}
          title="Pro or Agency plan required"
          description="Upgrade to a Pro or Agency plan to unlock the agent workspaces."
        />
      }
    >
      {children}
    </CapabilityGate>
  );
}

/** Remount routed pages when the active managed artist changes so every
 *  artist-scoped page reloads its own private data. */
function ArtistKeyed({ children }: { children: React.ReactNode }) {
  const { selectedArtistId } = useArtistScopeSafe();
  return <React.Fragment key={selectedArtistId ?? "none"}>{children}</React.Fragment>;
}

function RequireAuth({ children }: { children: React.ReactNode }) {
  const { isAuthenticated } = useAuth();
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  return <>{children}</>;
}

function CrmLeadRedirect() {
  const { leadId } = useParams();
  return <Navigate to={`/contacts/leads/${leadId ?? ""}`} replace />;
}

function CrmEventRedirect() {
  const { eventId } = useParams();
  return <Navigate to={`/contacts/events/${eventId ?? ""}`} replace />;
}

function CrmVenueRedirect() {
  const { venueId } = useParams();
  return <Navigate to={`/contacts/venues/${venueId ?? ""}`} replace />;
}

export function TrialExpiredGate({ children }: { children: React.ReactNode }) {
  const { trialExpired, session, user } = useSessionUser();
  const location = useLocation();

  const subscriptionActive = hasActiveSubscription(session as Record<string, unknown> | null);
  const isOwner = Boolean(user?.is_owner);

  // Allow billing page and settings so they can subscribe
  const isBillingPage = location.pathname.startsWith("/settings/billing") || location.pathname.startsWith("/settings");

  if (trialExpired && !subscriptionActive && !isOwner && !isBillingPage) {
    // Data stays visible (read-only where the plan requires it); paid actions
    // return a plan-required error that points here.
    return (
      <>
        <div
          role="alert"
          aria-label="Trial ended"
          className="mx-auto mt-3 flex w-full max-w-7xl flex-wrap items-center justify-between gap-3 rounded-xl border border-amber-500/40 bg-amber-500/10 px-4 py-3 text-sm text-foreground"
        >
          <span>
            <strong>Your free trial has ended.</strong> Your artists, venues and pipeline are saved. Choose a plan to
            keep discovering, saving and pitching venues.
          </span>
          <Link
            to="/settings/billing"
            className="rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground transition hover:opacity-90"
          >
            Choose a plan
          </Link>
        </div>
        {children}
      </>
    );
  }

  return <>{children}</>;
}

function ProtectedAppLayout() {
  const { status } = useOnboardingProgress();
  const location = useLocation();
  const navigate = useNavigate();
  const { profile, accessToken, completeLogin } = useAuth();

  useEffect(() => {
    if (!accessToken || !profile) return;
    if (!needsBillingContextRefresh(profile as Record<string, unknown>)) return;
    fetchCurrentAccount()
      .then((accountProfile) => {
        completeLogin(accessToken, { ...profile, ...accountProfile });
      })
      .catch(() => {
        // Best-effort refresh; app shell can proceed without it.
      });
  }, [accessToken, completeLogin, profile]);

  useEffect(() => {
    if (!status) return;
    if (!status.onboarding_completed) {
      const needsBilling = requiresBillingBeforeOnboarding(profile as Record<string, unknown> | null);
      const isBillingRoute = location.pathname.startsWith("/settings/billing");
      if (needsBilling) {
        if (!isBillingRoute) {
          navigate("/settings/billing", { replace: true });
        }
        return;
      }
      const targetStep = status.current_step || "account";
      if (!location.pathname.startsWith(`/signup/${targetStep}`)) {
        navigate(`/signup/${targetStep}`, { replace: true });
      }
      return;
    }
    if (location.pathname.startsWith("/signup")) {
      // Post-onboarding landing route: Dashboard
      navigate("/dashboard", { replace: true });
    }
  }, [status, location.pathname, navigate, profile]);

  return (
    <SessionUserProvider>
      <CapabilityProvider>
        <ArtistProvider>
          <BreadcrumbProvider>
            <ToastProvider>
              <FeatureFlagsProvider>
                <PodcastTourProvider>
                  <ActiveAgentContextProvider>
                    <div className="aa-app-shell min-h-screen bg-background text-foreground transition-colors duration-300">
                      <TopNav />
                      <TrialExpiredGate>
                      <main className="aa-page-shell mx-auto w-full max-w-7xl space-y-3 px-3 sm:px-4 py-3">
                        <Suspense fallback={<Loading />}>
                        <ArtistKeyed>
                        <Routes>
                          <Route path="/" element={<Navigate to="/dashboard" replace />} />
                          <Route path="/dashboard" element={<DashboardPage />} />
                          <Route path="/beta-admin" element={<OwnerRoute><BetaAdminPage /></OwnerRoute>} />
                          <Route path="/discover" element={<DiscoverPage />} />
                          <Route
                            path="/executive"
                            element={
                              <OwnerRoute>
                                <ExecutiveDashboard />
                              </OwnerRoute>
                            }
                          />
                          <Route
                            path="/control/master"
                            element={
                              <OwnerRoute>
                                <ProtectedRoute requiredPermissions={["view_workflows", "view_observability"]}>
                                  <MasterControlPage />
                                </ProtectedRoute>
                              </OwnerRoute>
                            }
                          />
                          <Route
                            path="/control/master/invites"
                            element={
                              <OwnerRoute>
                                <CapabilityGate flag="canViewMasterControl" variant="card" fallback={<LockedFeatureCard flag="canViewMasterControl" />}>
                                  <InvitesPage />
                                </CapabilityGate>
                              </OwnerRoute>
                            }
                          />
                          <Route
                            path="/control/master/registry"
                            element={
                              <OwnerRoute>
                                <CapabilityGate flag="canViewMasterControl" variant="card" fallback={<LockedFeatureCard flag="canViewMasterControl" />}>
                                  <CapabilityGate flag="canModifyGlobalSettings" fallback={<LockedFeatureCard flag="canModifyGlobalSettings" />}>
                                    <RegistryPage />
                                  </CapabilityGate>
                                </CapabilityGate>
                              </OwnerRoute>
                            }
                          />
                          <Route
                            path="/accounting"
                            element={
                              <OwnerRoute>
                                <ProtectedRoute requiredPermissions={["view_financials"]}>
                                  <AccountingPage />
                                </ProtectedRoute>
                              </OwnerRoute>
                            }
                          />
                          {/* MVP customer names: Business → Accounting, Shows → Calendar */}
                          <Route
                            path="/business"
                            element={
                              <OwnerRoute>
                                <ProtectedRoute requiredPermissions={["view_financials"]}>
                                  <AccountingPage />
                                </ProtectedRoute>
                              </OwnerRoute>
                            }
                          />
                          <Route path="/shows" element={<CalendarPage />} />
                          {/* CRM & Operations. Private beta: legacy Agent/CRM/Business surfaces are
                              owner-only (OwnerRoute → /dashboard); musicians keep Home, Discover, Booking
                              (incl. lead detail), Shows and Settings. */}
                          <Route path="/crm" element={<Navigate to="/contacts" replace />} />
                          <Route path="/contacts" element={<OwnerRoute><CrmPage /></OwnerRoute>} />
                          <Route path="/artists" element={<OwnerRoute><ArtistsPage /></OwnerRoute>} />
                          <Route path="/booking" element={<BookingPage />} />
                          <Route path="/crm/leads/:leadId" element={<CrmLeadRedirect />} />
                          <Route path="/crm/events/:eventId" element={<CrmEventRedirect />} />
                          <Route path="/crm/venues/:venueId" element={<CrmVenueRedirect />} />
                          <Route path="/discover/venues/:globalVenueId" element={<PublicVenuePage />} />
                          <Route path="/contacts/leads/:leadId" element={<LeadDetailPage />} />
                          <Route path="/contacts/events/:eventId" element={<OwnerRoute><EventDetailPage /></OwnerRoute>} />
                          <Route path="/contacts/venues/:venueId" element={<OwnerRoute><VenueDetailPage /></OwnerRoute>} />
                          <Route path="/calendar" element={<CalendarPage />} />
                          <Route path="/venue-map" element={<OwnerRoute><VenueMapPage /></OwnerRoute>} />
                          <Route path="/venue-queue" element={<OwnerRoute><VenueQueuePage /></OwnerRoute>} />
                          <Route
                            path="/venues/ingestion"
                            element={
                              <OwnerRoute>
                                <VenueIngestionPage />
                              </OwnerRoute>
                            }
                          />
                          <Route path="/email" element={<OwnerRoute><EmailPage /></OwnerRoute>} />
                          <Route path="/hitl/inbox" element={<OwnerRoute><HitlInboxPage /></OwnerRoute>} />
                          <Route
                            path="/routing"
                            element={
                              <OwnerRoute>
                                <CapabilityGate capability="canUseRoutingAI" variant="card" fallback={<LockedFeatureCard flag="canUseRoutingAI" />}>
                                  <RoutingPage />
                                </CapabilityGate>
                              </OwnerRoute>
                            }
                          />

                          {/* Unified AI Workspace hub — /agents?agent=<name> */}
                          <Route
                            path="/agents"
                            element={
                              <OwnerRoute>
                                <PlusTierGate>
                                  <AgentsHubPage />
                                </PlusTierGate>
                              </OwnerRoute>
                            }
                          />

                          {/* Per-agent deep links — kept for backwards-compat and direct sharing */}
                          {AGENT_ROUTE_CONFIG.map(({ name, capability, ownerOnly }) => {
                            const inner = capability ? (
                              <CapabilityGate capability={capability} variant="card" fallback={<LockedFeatureCard flag={capability} />}>
                                <AgentPage agentName={name} />
                              </CapabilityGate>
                            ) : (
                              <AgentPage agentName={name} />
                            );
                            return (
                              <Route
                                key={name}
                                path={`/agents/${name}`}
                                element={
                                  // Private beta: agent workspaces are operator-only surfaces.
                                  <OwnerRoute>{ownerOnly ? inner : <PlusTierGate>{inner}</PlusTierGate>}</OwnerRoute>
                                }
                              />
                            );
                          })}

                          {/* Observability & Memory */}
                          <Route
                            path="/agents/observability"
                            element={
                              <OwnerRoute>
                                <ProtectedRoute requiredPermissions={["view_observability"]}>
                                  <PlusTierGate>
                                    <AgentsObservabilityDashboard />
                                  </PlusTierGate>
                                </ProtectedRoute>
                              </OwnerRoute>
                            }
                          />
                          <Route
                            path="/observability/frontend"
                            element={
                              <OwnerRoute>
                                <ProtectedRoute requiredPermissions={["view_observability"]}>
                                  <CapabilityGate
                                    capability="observability.frontend"
                                    variant="card"
                                    fallback={
                                      <LockedFeatureCard
                                        flag="observability.frontend"
                                        title="Frontend health locked"
                                        description="Upgrade to monitor client-side error logs."
                                      />
                                    }
                                  >
                                    <FrontendErrorsPage />
                                  </CapabilityGate>
                                </ProtectedRoute>
                              </OwnerRoute>
                            }
                          />
                          <Route
                            path="/observability/frontend/live"
                            element={
                              <OwnerRoute>
                                <ProtectedRoute requiredPermissions={["view_observability"]}>
                                  <CapabilityGate
                                    capability="observability.frontend"
                                    variant="card"
                                    fallback={
                                      <LockedFeatureCard
                                        flag="observability.frontend"
                                        title="Frontend stream locked"
                                        description="Upgrade to watch errors as they arrive."
                                      />
                                    }
                                  >
                                    <RealTimeFrontendErrorsPage />
                                  </CapabilityGate>
                                </ProtectedRoute>
                              </OwnerRoute>
                            }
                          />
                          <Route path="/memory" element={<OwnerRoute><MemoryConsolePage /></OwnerRoute>} />
                          <Route path="/memory/graph" element={<Navigate to="/memory?tab=graph" replace />} />
                          <Route path="/memory/episodes" element={<Navigate to="/memory?tab=episodes" replace />} />

                          {/* Automations */}
                          <Route
                            path="/workflows/builder"
                            element={
                              <OwnerRoute>
                                <WorkflowBuilder />
                              </OwnerRoute>
                            }
                          />
                          <Route
                            path="/automation/triggers"
                            element={
                              <OwnerRoute>
                                <ProtectedRoute requiredPermissions={["view_workflows"]}>
                                  <WorkflowTriggerHistoryPage />
                                </ProtectedRoute>
                              </OwnerRoute>
                            }
                          />

                          {/* Studios & Marketing */}
                          <Route path="/mixboard" element={<OwnerRoute><MixboardPage /></OwnerRoute>} />
                          <Route
                            path="/marketing/library"
                            element={
                              <OwnerRoute>
                                <PlusTierGate>
                                  <MarketingLibraryPage />
                                </PlusTierGate>
                              </OwnerRoute>
                            }
                          />
                          <Route
                            path="/marketing/music-analytics/track/:trackId"
                            element={
                              <OwnerRoute>
                                <MusicAnalyticsTrackPage />
                              </OwnerRoute>
                            }
                          />
                          <Route
                            path="/marketing/music-analytics"
                            element={
                              <OwnerRoute>
                                <MusicAnalyticsPage />
                              </OwnerRoute>
                            }
                          />
                          <Route
                            path="/pressroom"
                            element={
                              <OwnerRoute>
                                <PressroomPage />
                              </OwnerRoute>
                            }
                          />

                          {/* Settings */}
                          <Route path="/settings" element={<AccountSettingsHubPage />} />
                          <Route path="/settings/account" element={<Navigate to="/settings" replace />} />
                          <Route path="/settings/artist" element={<ArtistSettingsPage />} />
                          <Route path="/settings/email" element={<OwnerRoute><EmailSettingsPage /></OwnerRoute>} />
                          <Route
                            path="/settings/org"
                            element={
                              <ProtectedRoute requiredPermissions={["manage_agents", "manage_strategic_goals"]}>
                                <OrgSettingsPage />
                              </ProtectedRoute>
                            }
                          />

                            <Route path="/settings/billing" element={<BillingSettingsPage />} />
                          <Route path="*" element={<NotFoundPage />} />
                        </Routes>
                        </ArtistKeyed>
                      </Suspense>
                    </main>
                    </TrialExpiredGate>
                  </div>
                  </ActiveAgentContextProvider>
                </PodcastTourProvider>
              </FeatureFlagsProvider>
            </ToastProvider>
          </BreadcrumbProvider>
        </ArtistProvider>
      </CapabilityProvider>
    </SessionUserProvider>
  );
}

const PUBLIC_DOMAINS = ["artistascend.app", "www.artistascend.app", "artistnav.com", "www.artistnav.com"];

function PublicOrAuthCatchAll() {
  const hostname = window.location.hostname;
  if (PUBLIC_DOMAINS.includes(hostname)) {
    return <NotFoundPublic />;
  }
  return (
    <RequireAuth>
      <ProtectedAppLayout />
    </RequireAuth>
  );
}

export default function App() {
  return (
    <ErrorBoundary>
    <I18nProvider>
      <ThemeProvider>
        <SwrProvider>
          <BrowserRouter>
            <ScrollToTop />
            <AuthProvider>
              <Suspense fallback={<Loading />}>
                <Routes>
                  <Route path="/" element={window.location.hostname === "artistnav.com" ? <ArtistNavLanding /> : window.location.hostname === "app.artistnav.com" ? <Navigate to="/dashboard" replace /> : <Navigate to="/hello" replace />} />
                  <Route path="/hello-beta" element={<ArtistNavLanding />} />
                  <Route path="/beta" element={<BetaApplyPage />} />
                  <Route path="/join" element={<BetaJoinPage />} />
                  <Route path="/collector/authorize" element={<CollectorAuthorize />} />
                  <Route path="/collector/connect" element={<CollectorConnect />} />
                  <Route path="/login" element={<LoginPage />} />
                  <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                  <Route path="/reset-password" element={<ResetPasswordPage />} />
                  <Route path="/two-factor" element={<TwoFactorPage />} />
                  <Route path="/claim" element={<ClaimInvitePage />} />
                  <Route path="/hello" element={window.location.hostname.endsWith("artistnav.com") ? <Navigate to="/" replace /> : <HelloLanding />} />
                  {HelloLandingBeta && <Route path="/hello-beta" element={<HelloLandingBeta />} />}
                  <Route
                    path="/signup/*"
                    element={
                      <RequireAuth>
                        <ToastProvider>
                          <OnboardingFlow />
                        </ToastProvider>
                      </RequireAuth>
                    }
                  />
                  <Route
                    path="*"
                    element={<PublicOrAuthCatchAll />}
                  />
                </Routes>
              </Suspense>
            </AuthProvider>
          </BrowserRouter>
        </SwrProvider>
      </ThemeProvider>
    </I18nProvider>
    </ErrorBoundary>
  );
}
